import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { KARBONAT_FARBEN, KARBONAT_STUFEN, KarbonatStufe } from './karbonat-stufen';

/**
 * Hilfe zum Karbonatgehalt einer Substratlage. Die Spalte hatte vorher kein (i).
 *
 * Die Stufen stehen in SEA 95, Teil B, Darst. B 3-18 (S. 76) — zusammen mit der
 * Reaktion auf Salzsäure, die im Gelände die eigentliche Ansprache ist. Für k4
 * bis k6 nennt die Anweisung keine Reaktion; dort bleibt die Spalte leer.
 */
@Component({
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Karbonatgehalt</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<p class="einleitung">
				Karbonat lässt sich im Gelände leicht mit <strong>Salzsäure</strong> ansprechen:
				Wie stark und wie lange die Probe aufbraust, sagt die Stufe.
			</p>
			<p class="basis">
				Angegeben ist der Gehalt der Lage in <strong>Masse-%</strong> — wie beim
				Humusgehalt, anders als beim Skelettgehalt (Volumen-%).
			</p>

			<table class="table table-sm karbonat-tabelle">
				<thead>
					<tr>
						<th class="eingabe-spalte">Eingabe</th>
						<th class="code-spalte">Kurzzeichen</th>
						<th class="spanne-spalte">Masse-%</th>
						<th>Bezeichnung</th>
						<th class="reaktion-spalte">Reaktion mit Salzsäure</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let s of stufen">
						<td class="eingabe-spalte">
							<span class="farbfeld" [style.background]="farbe(s)"></span>
							<span class="wert">{{ s.wert }}</span>
						</td>
						<td class="code-spalte"><code>{{ s.code }}</code></td>
						<td class="spanne-spalte">{{ s.spanne }}</td>
						<td>
							{{ s.bezeichnung }}
							<span class="auch" *ngIf="s.auch">= {{ s.auch }}</span>
						</td>
						<td class="reaktion-spalte">
							<span *ngIf="s.reaktion">{{ s.reaktion }}</span>
							<span *ngIf="!s.reaktion" class="leer" title="Die Anweisung nennt hier keine Reaktion">—</span>
						</td>
					</tr>
				</tbody>
			</table>

			<p class="hinweis">
				Für die Stufen k4 bis k6 nennt die Anweisung keine eigene Reaktion — dort trennt
				die Salzsäureprobe die Stufen nicht mehr.
			</p>

			<div class="block">
				<h6>Was die Stufe bewirkt</h6>
				<p class="mb-0">
					Aus der Substratfolge wird die <strong>Karbonattiefe</strong> berechnet: die
					Oberkante der ersten Lage mit Karbonat, also der ersten Lage ab
					<code>k1</code>. Trägt schon die oberste Lage Karbonat, ist die Tiefe 0; trägt
					keine Lage welches, bleibt das Feld leer.
				</p>
			</div>

			<p class="hinweis">
				Die Abstufung weicht von der Kartieranleitung 1995 der geologischen Landesämter
				ab; die Anweisung begründet das mit dem bereits vorhandenen Aufnahmefundus.
			</p>

			<p class="quelle">
				Quelle: SEA 95, Teil B, Darst. B 3-18 „Gehalt an karbonatischer Substanz in
				Masse-%", S. 76.
			</p>
		</div>
	`,
	styles: [`
		.modal-body {
			padding: 1rem 1.5rem;
		}
		.einleitung {
			margin-bottom: 0.5rem;
		}
		.basis {
			font-size: 0.88rem;
			color: #495057;
			margin-bottom: 1rem;
		}
		.karbonat-tabelle {
			margin-bottom: 0.75rem;
		}
		.karbonat-tabelle th {
			background: #f8f9fa;
			font-weight: 600;
			white-space: nowrap;
		}
		.karbonat-tabelle td,
		.karbonat-tabelle th {
			vertical-align: middle;
		}
		.eingabe-spalte {
			width: 6rem;
			white-space: nowrap;
		}
		.farbfeld {
			display: inline-block;
			width: 1.1rem;
			height: 1.1rem;
			border: 1px solid #adb5bd;
			border-radius: 3px;
			vertical-align: middle;
			margin-right: 0.5rem;
		}
		.wert {
			font-weight: 600;
			font-size: 1rem;
		}
		.code-spalte {
			width: 6rem;
			white-space: nowrap;
		}
		.karbonat-tabelle code,
		.block code {
			font-size: 0.95rem;
			color: #212529;
			background: #f1f3f5;
			border-radius: 3px;
			padding: 0.1rem 0.4rem;
		}
		.spanne-spalte {
			width: 7rem;
			white-space: nowrap;
			text-align: right;
			padding-right: 1.25rem;
		}
		.auch {
			display: block;
			color: #6c757d;
			font-size: 0.85rem;
		}
		.reaktion-spalte {
			width: 13rem;
		}
		.leer {
			color: #adb5bd;
		}
		.hinweis {
			font-size: 0.85rem;
			color: #495057;
			margin-bottom: 1rem;
		}
		.block {
			border: 1px solid #dee2e6;
			border-radius: 4px;
			padding: 0.75rem 1rem;
			margin-bottom: 1rem;
			background: #f8f9fa;
		}
		.block h6 {
			font-weight: 600;
			margin-bottom: 0.5rem;
		}
		.block p {
			font-size: 0.9rem;
		}
		.quelle {
			font-size: 0.82rem;
			color: #6c757d;
			margin-bottom: 0;
		}
	`]
})
export class KarbonatgehaltInfoModalComponent {
	activeModal = inject(NgbActiveModal);

	stufen = KARBONAT_STUFEN;

	/**
	 * Von farblos nach kalkweiß-blau — nur zur Orientierung beim Suchen der Zeile.
	 * Dieselbe Reihe färbt das Feld in der Substrattabelle (KARBONAT_FARBEN in
	 * karbonat-stufen.ts).
	 */
	farbe(s: KarbonatStufe): string {
		return KARBONAT_FARBEN[s.wert] ?? '#f8f9fa';
	}
}
